import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { mapMemberContext } from './calendarMemberContext.utils';
import { CalendarMemberContextControls, CalendarMemberContextView } from './CalendarMemberContextView';
import { useCalendarMemberDetails } from './useCalendarMemberDetails';

export default function CalendarMemberContext({ member, onBack, onClose, onNavigateAway }) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const token = sessionStorage.getItem('token') || null;
  const { data, isLoading, hasError } = useCalendarMemberDetails(member?.id, token);
  const locale = i18n.resolvedLanguage || i18n.language || 'en';
  const context = React.useMemo(() => mapMemberContext(data, member, locale), [data, locale, member]);

  const openMemberPage = (path) => {
    onNavigateAway?.();
    onClose?.();
    navigate(`${path}/${member.id}`, {
      state: {
        breadcrumbName: context.name,
        origin: 'calendar',
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const openProfile = () => {
    const query = new URLSearchParams({
      origin: 'calendar',
      returnTo: `${location.pathname}${location.search}${location.hash}`,
    });
    const profileWindow = window.open(
      `/common/personal-details/${member.id}?${query.toString()}`,
      '_blank'
    );
    if (profileWindow) profileWindow.opener = null;
  };
  const openActivityLog = () => openMemberPage('/common/activity-log');

  const copyValue = React.useCallback((value) => {
    if (value) navigator.clipboard?.writeText(value).catch(() => {});
  }, []);

  if (isLoading || hasError || !data) {
    return (
      <div className="calendar-member-context">
        <CalendarMemberContextControls onBack={onBack} onClose={onClose} t={t} />
        {isLoading ? <p>{t('common-loading')}</p> : <p className="error-message">{t('calendar-member-load-error')}</p>}
      </div>
    );
  }

  return (
    <CalendarMemberContextView
      member={context}
      onBack={onBack}
      onClose={onClose}
      onCopy={copyValue}
      onProfileClick={openProfile}
      onHistoryClick={openActivityLog}
      t={t}
    />
  );
}
